/**
 * Datasource remoto Plans: abstração + implementação Firestore.
 * Recebe Firestore por injeção (clean architecture); se não passado, usa getFirestoreDb().
 */

import type { Firestore } from 'firebase/firestore';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/core/services';
import { normalizePlanFields, Plan, PlanRef, planSchema } from '@/core/domain';

export interface PlanRemoteDataSource {
  getById(id: string): Promise<Plan | null>;
  getAll(): Promise<Plan[]>;
  create(plan: Plan): Promise<Plan>;
  update(id: string, plan: Partial<Plan>): Promise<Plan>;
  delete(id: string): Promise<void>;
}

function toPlan(id: string, data: Record<string, unknown>): Plan {
  const parsed = planSchema.safeParse(normalizePlanFields(id, data));
  if (!parsed.success) throw new Error('Invalid plan from Firestore');
  return parsed.data;
}

export class PlanFirestoreDataSource implements PlanRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  async getById(id: string): Promise<Plan | null> {
    const ref = doc(this.getDb(), PlanRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return toPlan(snap.id, snap.data() as Record<string, unknown>);
  }

  async getAll(): Promise<Plan[]> {
    const ref = collection(this.getDb(), PlanRef.collection);
    const snap = await getDocs(ref);
    return snap.docs.map((d) => toPlan(d.id, d.data() as Record<string, unknown>));
  }

  async create(plan: Plan): Promise<Plan> {
    const ref = doc(this.getDb(), PlanRef.collection, plan.id);
    await setDoc(ref, plan);
    return plan;
  }

  async update(id: string, plan: Partial<Plan>): Promise<Plan> {
    const ref = doc(this.getDb(), PlanRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Plan not found');
    const current = toPlan(snap.id, snap.data() as Record<string, unknown>);
    const updated = { ...current, ...plan, id };
    await setDoc(ref, updated);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const ref = doc(this.getDb(), PlanRef.collection, id);
    await deleteDoc(ref);
  }
}
